import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SoupKitchenIcon from '@mui/icons-material/SoupKitchen';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import Layout from '../components/Layout';
import { kochSearch, KochResult, apiErrorText, fetchSupplierContacts, saveSupplierContact } from '../api/client';

const EXAMPLES = ['Leber', 'Pommes', 'Spargel', 'Salat', 'Brokkoli'];
const STALE_DAYS = 56; // ~8 Wochen

const fmtEUR = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

const fmtDate = (s: string | null | undefined) =>
  !s ? null : new Date(s).toLocaleDateString('de-DE');

/** Weeks since `last_seen`, or null if not stale / unparseable. */
const staleWeeks = (s: string | null | undefined): number | null => {
  if (!s) return null;
  const then = new Date(s).getTime();
  if (Number.isNaN(then)) return null;
  const days = (Date.now() - then) / (1000 * 60 * 60 * 24);
  if (days <= STALE_DAYS) return null;
  return Math.floor(days / 7);
};

export default function KochSearchPage() {
  const theme = useTheme();
  const [q, setQ] = useState('');
  const [results, setResults] = useState<KochResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const debounceRef = useRef<number | null>(null);

  // Debounced search (300ms, min 2 chars)
  const triggerSearch = useCallback(async (val: string) => {
    if (val.trim().length < 2) {
      setResults([]);
      setHasSearched(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await kochSearch(val.trim(), 30);
      setResults(res.results);
      setHasSearched(true);
    } catch (e: any) {
      setError(apiErrorText(e) || 'Suche fehlgeschlagen — bitte nochmal probieren.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => triggerSearch(q), 300);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [q, triggerSearch]);

  // Group by cluster — the chef thinks in products, not SKUs.
  const groups = useMemo(() => {
    const map = new Map<string, KochResult[]>();
    for (const r of results) {
      const key = r.cluster_id || `${r.supplier}::${r.article_no}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(r);
    }
    // Within each group: Bestpreis first, then cheapest-to-priciest.
    for (const arr of map.values()) {
      arr.sort((a, b) => {
        if (a.is_bestpreis && !b.is_bestpreis) return -1;
        if (!a.is_bestpreis && b.is_bestpreis) return 1;
        return a.pct_vs_bestpreis - b.pct_vs_bestpreis;
      });
    }
    // Groups sorted by their cheapest unit price ascending.
    return Array.from(map.entries()).sort(([, a], [, b]) => {
      const ap = a[0]?.unit_price_per_einheit ?? Infinity;
      const bp = b[0]?.unit_price_per_einheit ?? Infinity;
      return ap - bp;
    });
  }, [results]);

  // supplier -> WhatsApp number (grows through use: entered once, saved for all)
  const [contacts, setContacts] = useState<Record<string, string>>({});
  useEffect(() => { fetchSupplierContacts().then(setContacts).catch(() => {}); }, []);
  const [waAsk, setWaAsk] = useState<{ supplier: string; msg: string } | null>(null);
  const [waPhone, setWaPhone] = useState('');

  const openWa = (phone: string, msg: string) => {
    window.open('https://wa.me/' + phone + '?text=' + encodeURIComponent(msg), '_blank');
  };

  const confirmWaPhone = async () => {
    if (!waAsk) return;
    const digits = waPhone.replace(/\D/g, '');
    if (digits.length < 8) return;
    try {
      await saveSupplierContact(waAsk.supplier, digits);
      setContacts(prev => ({ ...prev, [waAsk.supplier]: digits }));
    } catch { /* trotzdem öffnen */ }
    openWa(digits, waAsk.msg);
    setWaAsk(null);
    setWaPhone('');
  };

  const openWhatsApp = (row: KochResult, bestpreis?: KochResult) => {
    const lines: string[] = ['Hallo,', ''];
    if (bestpreis && !row.is_bestpreis) {
      // Pricier supplier → use the Bestpreis as leverage.
      lines.push(
        `ich vergleiche gerade die Preise für ${row.cluster_name}.`,
        `Bei euch zahle ich aktuell ${fmtEUR(row.unit_price_per_einheit)} pro ${row.normalized_unit || 'Einheit'}.`,
        `Bei ${bestpreis.supplier} bekomme ich das Gleiche für ${fmtEUR(bestpreis.unit_price_per_einheit)} pro ${bestpreis.normalized_unit || 'Einheit'}.`,
        '',
        'Geht da preislich noch was?'
      );
    } else {
      lines.push(
        `kurze Frage zu ${row.cluster_name} (${row.article_name}, Art-Nr ${row.article_no}):`,
        `Aktuell zahlen wir ${fmtEUR(row.unit_price_per_einheit)} pro ${row.normalized_unit || 'Einheit'}.`,
        '',
        'Bekommen wir da einen besseren Preis?'
      );
    }
    lines.push('', 'Danke und Gruß');
    const msg = lines.join('\n');
    const phone = contacts[row.supplier];
    if (phone) {
      openWa(phone, msg);
    } else {
      setWaAsk({ supplier: row.supplier, msg });
      setWaPhone('');
    }
  };

  const showEmptyPrompt = q.trim().length < 2 && !hasSearched;
  const greenTint = alpha(theme.palette.success.main, 0.07);
  const green = theme.palette.success.main;

  return (
    <Layout>
      <Box sx={{ maxWidth: 660, mx: 'auto', pb: 8 }}>
        {/* ─── Search hero ─── */}
        <Box sx={{ textAlign: 'center', pt: { xs: 1, sm: 3 }, mb: 2.5 }}>
          <Typography
            variant="h4"
            sx={{ fontWeight: 800, fontSize: { xs: '1.6rem', sm: '2rem' }, mb: 0.5 }}
          >
            Wer ist am günstigsten?
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Produkt eintippen — Skonto zeigt dir sofort den besten Preis.
          </Typography>
        </Box>

        <TextField
          fullWidth
          size="medium"
          autoFocus
          type="search"
          autoComplete="off"
          placeholder="z. B. Leber, Pommes, Spargel …"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {loading ? (
                  <CircularProgress size={24} color="secondary" />
                ) : (
                  <SearchIcon sx={{ fontSize: 26, color: 'text.secondary' }} />
                )}
              </InputAdornment>
            ),
            endAdornment: q ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Suche löschen"
                  onClick={() => setQ('')}
                  sx={{ width: 44, height: 44 }}
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ) : undefined,
            sx: {
              fontSize: 20,
              fontWeight: 600,
              borderRadius: 4,
              bgcolor: 'background.paper',
              minHeight: 60,
              px: 1.5,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              '& fieldset': { borderColor: 'transparent' },
              '&:hover fieldset': { borderColor: 'secondary.light' },
              '&.Mui-focused fieldset': { borderColor: 'secondary.main', borderWidth: 2 },
            },
          }}
          sx={{ mb: 2.5 }}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }}>
            {error}
          </Alert>
        )}

        {/* ─── Empty state: warm prompt + example chips ─── */}
        {showEmptyPrompt && !error && (
          <Stack spacing={2} alignItems="center" sx={{ mt: 5, textAlign: 'center' }}>
            <SoupKitchenIcon sx={{ fontSize: 56, color: 'secondary.main', opacity: 0.85 }} />
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 340 }}>
              Was brauchst du gerade? Probier zum Beispiel:
            </Typography>
            <Stack
              direction="row"
              spacing={0}
              useFlexGap
              flexWrap="wrap"
              justifyContent="center"
              sx={{ gap: 1.25, maxWidth: 420 }}
            >
              {EXAMPLES.map((ex) => (
                <Chip
                  key={ex}
                  label={ex}
                  clickable
                  onClick={() => setQ(ex)}
                  sx={{
                    height: 44,
                    px: 1,
                    fontSize: 16,
                    fontWeight: 600,
                    borderRadius: '22px',
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
                    '&:hover': { borderColor: 'secondary.main', bgcolor: alpha(theme.palette.secondary.main, 0.06) },
                  }}
                />
              ))}
            </Stack>
          </Stack>
        )}

        {/* ─── No results ─── */}
        {hasSearched && results.length === 0 && !loading && !error && (
          <Stack spacing={1.5} alignItems="center" sx={{ mt: 5, textAlign: 'center' }}>
            <SearchOffIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Nichts gefunden für „{q.trim()}“
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320 }}>
              Probier ein kürzeres Wort — z. B. „Leber“ statt „Kalbsleber geschnitten“.
            </Typography>
          </Stack>
        )}

        {/* ─── Results: one card per product, Bestpreis as hero ─── */}
        <Stack spacing={2.5}>
          {groups.map(([key, items]) => {
            const best = items[0];
            const alternatives = items.slice(1);
            const bestStale = staleWeeks(best.last_seen);
            const bestDate = fmtDate(best.last_seen);

            return (
              <Paper
                key={key}
                elevation={0}
                sx={{
                  borderRadius: 4,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                }}
              >
                {/* Product name */}
                <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 800, fontSize: { xs: '1.15rem', sm: '1.25rem' }, lineHeight: 1.25 }}
                  >
                    {best.cluster_name}
                  </Typography>
                  {best.category && (
                    <Typography variant="caption" color="text.secondary">
                      {best.category}
                    </Typography>
                  )}
                </Box>

                {/* ── Bestpreis hero row ── */}
                <Box
                  sx={{
                    mx: 1.25,
                    mb: alternatives.length ? 1 : 1.25,
                    p: 2,
                    borderRadius: 3,
                    bgcolor: greenTint,
                    border: '2px solid',
                    borderColor: green,
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Chip
                      icon={<EmojiEventsIcon sx={{ fontSize: 18 }} />}
                      label="BESTPREIS"
                      size="small"
                      sx={{
                        bgcolor: 'success.main',
                        color: '#fff',
                        fontWeight: 800,
                        letterSpacing: 0.6,
                        '& .MuiChip-icon': { color: '#fff' },
                      }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }} noWrap>
                      {best.supplier}
                    </Typography>
                  </Stack>

                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1.5}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: { xs: '1.9rem', sm: '2.1rem' },
                          lineHeight: 1.1,
                          color: 'success.main',
                        }}
                      >
                        {fmtEUR(best.unit_price_per_einheit)}
                        <Typography
                          component="span"
                          sx={{ fontSize: '1rem', fontWeight: 600, color: 'text.secondary', ml: 0.5 }}
                        >
                          / {best.normalized_unit || 'Einheit'}
                        </Typography>
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.5 }}
                        noWrap
                      >
                        {best.article_name}
                        {bestDate ? ` · Stand ${bestDate}` : ''}
                      </Typography>
                    </Box>

                    <IconButton
                      aria-label={`WhatsApp an ${best.supplier}`}
                      onClick={() => openWhatsApp(best)}
                      sx={{
                        width: 52,
                        height: 52,
                        flexShrink: 0,
                        bgcolor: 'success.main',
                        color: '#fff',
                        '&:hover': { bgcolor: 'success.dark' },
                      }}
                    >
                      <WhatsAppIcon sx={{ fontSize: 28 }} />
                    </IconButton>
                  </Stack>

                  {bestStale != null && (
                    <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ mt: 1 }}>
                      <HourglassBottomIcon sx={{ fontSize: 16, color: 'warning.main', mt: '1px' }} />
                      <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 600 }}>
                        Preis ist {bestStale} Wochen alt — evtl. beim Lieferanten gegenchecken.
                      </Typography>
                    </Stack>
                  )}
                </Box>

                {/* ── Pricier alternatives (quiet rows) ── */}
                {alternatives.length > 0 && (
                  <Box sx={{ px: 1.25, pb: 1.25 }}>
                    {alternatives.map((r, i) => {
                      const stale = staleWeeks(r.last_seen);
                      const date = fmtDate(r.last_seen);
                      return (
                        <Box
                          key={`${r.supplier}-${r.article_no}-${i}`}
                          sx={{
                            px: 1,
                            py: 1.25,
                            borderTop: i === 0 ? 'none' : '1px solid',
                            borderColor: 'divider',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            minHeight: 56,
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Stack direction="row" alignItems="baseline" spacing={1} sx={{ flexWrap: 'wrap' }}>
                              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                {fmtEUR(r.unit_price_per_einheit)}
                                <Typography
                                  component="span"
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ ml: 0.5 }}
                                >
                                  / {r.normalized_unit || 'Einheit'}
                                </Typography>
                              </Typography>
                              {r.pct_vs_bestpreis > 0 && (
                                <Typography
                                  variant="body2"
                                  sx={{ color: 'warning.main', fontWeight: 700, whiteSpace: 'nowrap' }}
                                >
                                  +{r.pct_vs_bestpreis.toFixed(0)} % teurer
                                </Typography>
                              )}
                            </Stack>
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                              {r.supplier}
                              {date ? ` · Stand ${date}` : ''}
                            </Typography>
                            {stale != null && (
                              <Typography
                                variant="caption"
                                sx={{ color: 'warning.main', fontWeight: 600, display: 'block' }}
                              >
                                ⏳ Preis ist {stale} Wochen alt — evtl. gegenchecken.
                              </Typography>
                            )}
                          </Box>

                          <IconButton
                            aria-label={`WhatsApp an ${r.supplier} — verhandeln`}
                            onClick={() => openWhatsApp(r, best)}
                            sx={{
                              width: 48,
                              height: 48,
                              flexShrink: 0,
                              color: 'success.main',
                              border: '1.5px solid',
                              borderColor: alpha(green, 0.4),
                              '&:hover': { bgcolor: greenTint, borderColor: 'success.main' },
                            }}
                          >
                            <WhatsAppIcon />
                          </IconButton>
                        </Box>
                      );
                    })}

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', px: 1, pt: 0.75 }}
                    >
                      Tipp: Tippe auf <WhatsAppIcon sx={{ fontSize: 13, verticalAlign: 'text-bottom', color: 'success.main' }} /> und
                      verhandle mit dem Bestpreis als Argument.
                    </Typography>
                  </Box>
                )}
              </Paper>
            );
          })}
        </Stack>
      </Box>
      <Dialog open={!!waAsk} onClose={() => setWaAsk(null)} fullWidth maxWidth="xs">
        <DialogTitle>WhatsApp-Nummer von {waAsk?.supplier}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Für diesen Lieferanten ist noch keine Nummer hinterlegt. Einmal eintragen — dann geht es beim nächsten Mal direkt.
          </Typography>
          <TextField
            autoFocus fullWidth label="Nummer (international, z. B. 4915112345678)"
            value={waPhone} onChange={(e) => setWaPhone(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWaAsk(null)}>Abbrechen</Button>
          <Button variant="contained" disabled={waPhone.replace(/\D/g, '').length < 8} onClick={confirmWaPhone}>
            Speichern & öffnen
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
